import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { AdminHeader, Drawer } from '@/features/admin/ui';
import { slugify } from '@/features/composer/types';
import {
  canDeleteCategory,
  catalogMutations,
  catalogQueries,
  nextSortOrder,
  reorderSwap,
  type Category,
} from '@/lib/queries/catalog';

export const Route = createFileRoute('/admin/categories')({
  component: CategoriesPage,
});

function CategoriesPage() {
  const qc = useQueryClient();
  const list = useQuery(catalogQueries.categories());
  const counts = useQuery(catalogQueries.categoryAssignedCounts());
  const rows = [...(list.data ?? [])].sort((a, b) => a.sort_order - b.sort_order);
  const assigned = counts.data ?? {};
  const [editing, setEditing] = useState<Partial<Category> | null>(null);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['catalog', 'categories'] });
    void qc.invalidateQueries({ queryKey: ['catalog', 'category-counts'] });
  };

  const reorder = useMutation({
    mutationFn: async ({ row, dir }: { row: Category; dir: -1 | 1 }) => {
      const swap = reorderSwap(rows, row.id, dir);
      if (!swap) return;
      await catalogMutations.reorderCategories(swap);
    },
    onSuccess: invalidate,
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Reorder failed.'),
  });

  const toggleVisible = useMutation({
    mutationFn: async (row: Category) =>
      catalogMutations.saveCategory({
        id: row.id,
        name: row.name,
        slug: row.slug,
        is_visible: !row.is_visible,
        sort_order: row.sort_order,
      }),
    onSuccess: invalidate,
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Update failed.'),
  });

  const remove = useMutation({
    mutationFn: async (row: Category) => {
      if (!canDeleteCategory(assigned[row.id] ?? 0)) {
        throw new Error('Reassign its videos before deleting this category.');
      }
      await catalogMutations.deleteCategory(row.id);
    },
    onSuccess: () => {
      toast.success('Category deleted.');
      invalidate();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Delete failed.'),
  });

  return (
    <div className="mx-auto max-w-4xl">
      <AdminHeader kicker="Admin · Browse" title="Categories" />
      <p className="mt-4 font-sans text-sm text-ink/60">
        Categories drive the rows on the streaming browse page. There is no seed data — an empty
        list collapses the sidebar panel. A category can&apos;t be deleted while videos are assigned
        to it.
      </p>

      <div className="mt-6 flex justify-end">
        <button
          type="button"
          onClick={() => setEditing({ is_visible: true, sort_order: nextSortOrder(rows) })}
          className="rounded-md border border-ink/20 px-3 py-1.5 font-sans text-xs font-medium uppercase tracking-[0.12em] text-ink hover:border-oxblood hover:text-oxblood"
        >
          Add category
        </button>
      </div>

      {list.isLoading ? (
        <p className="mt-8 text-center font-sans text-sm text-ink/50">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="mt-8 text-center font-sans text-sm text-ink/50">
          No categories yet. Add one to start building browse rows.
        </p>
      ) : (
        <ul className="mt-3 divide-y divide-ink/10">
          {rows.map((r, i) => {
            const inUse = (assigned[r.id] ?? 0) > 0;
            return (
              <li key={r.id} className="flex items-center gap-3 py-3">
                <span className="flex flex-col">
                  <button
                    type="button"
                    disabled={i === 0}
                    onClick={() => reorder.mutate({ row: r, dir: -1 })}
                    className="text-ink/40 hover:text-oxblood disabled:opacity-20"
                    aria-label="Move up"
                  >
                    <ChevronUp className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    disabled={i === rows.length - 1}
                    onClick={() => reorder.mutate({ row: r, dir: 1 })}
                    className="text-ink/40 hover:text-oxblood disabled:opacity-20"
                    aria-label="Move down"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </button>
                </span>
                <button type="button" onClick={() => setEditing(r)} className="flex-1 text-left">
                  <span className="font-serif text-base text-ink">{r.name}</span>
                  <span className="ml-2 font-sans text-xs text-ink/40">/{r.slug}</span>
                  {inUse && (
                    <span className="ml-2 font-sans text-xs text-ink/40">
                      · {assigned[r.id]} video{assigned[r.id] === 1 ? '' : 's'}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => toggleVisible.mutate(r)}
                  className={`rounded-sm px-2 py-0.5 font-sans text-[0.7rem] uppercase tracking-[0.12em] ${
                    r.is_visible ? 'bg-emerald-600/10 text-emerald-700' : 'bg-ink/10 text-ink/50'
                  }`}
                >
                  {r.is_visible ? 'Visible' : 'Hidden'}
                </button>
                <button
                  type="button"
                  disabled={inUse || remove.isPending}
                  onClick={() => remove.mutate(r)}
                  title={inUse ? 'Reassign its videos first' : undefined}
                  className="font-sans text-xs text-oxblood hover:underline disabled:cursor-not-allowed disabled:text-ink/30 disabled:no-underline"
                >
                  Delete
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <Drawer
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={editing?.id ? 'Edit category' : 'New category'}
      >
        {editing && (
          <CategoryForm
            category={editing}
            onDone={() => {
              setEditing(null);
              invalidate();
            }}
          />
        )}
      </Drawer>
    </div>
  );
}

function CategoryForm({ category, onDone }: { category: Partial<Category>; onDone: () => void }) {
  const [name, setName] = useState(category.name ?? '');
  const [slug, setSlug] = useState(category.slug ?? '');
  const [slugTouched, setSlugTouched] = useState(Boolean(category.slug));
  const [isVisible, setIsVisible] = useState(category.is_visible ?? true);

  const save = useMutation({
    mutationFn: async () => {
      const trimmedName = name.trim();
      const finalSlug = (slugTouched ? slug : slugify(name)).trim();
      if (!trimmedName) throw new Error('Name is required.');
      if (!finalSlug) throw new Error('Slug is required.');
      await catalogMutations.saveCategory({
        id: category.id,
        name: trimmedName,
        slug: finalSlug,
        is_visible: isVisible,
        sort_order: category.sort_order ?? 0,
      });
    },
    onSuccess: () => {
      toast.success('Category saved.');
      onDone();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Save failed.'),
  });

  return (
    <div className="space-y-4">
      <Field label="Name">
        <input
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (!slugTouched) setSlug(slugify(e.target.value));
          }}
          className="w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
        />
      </Field>
      <Field label="Slug">
        <input
          value={slug}
          onChange={(e) => {
            setSlugTouched(true);
            setSlug(slugify(e.target.value));
          }}
          className="w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
        />
      </Field>
      <label className="flex items-center gap-2 font-sans text-sm text-ink/80">
        <input
          type="checkbox"
          checked={isVisible}
          onChange={(e) => setIsVisible(e.target.checked)}
          className="accent-oxblood"
        />
        Visible on the browse page
      </label>
      <button
        type="button"
        disabled={save.isPending}
        onClick={() => save.mutate()}
        className="rounded-md bg-ink px-4 py-2 font-sans text-sm font-medium text-parchment hover:bg-oxblood disabled:opacity-60"
      >
        Save category
      </button>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block font-sans text-xs uppercase tracking-[0.12em] text-ink/60">
        {label}
      </label>
      {children}
    </div>
  );
}
